import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import {
  collection, getDocs, addDoc, doc, updateDoc, getDoc,
  serverTimestamp, query, where
} from 'firebase/firestore';
import { useNavigate, useParams } from 'react-router-dom';

function FolderForm() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [studentSearch, setStudentSearch] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    type: 'students', // 'students' | 'courses' | 'general'
    studentIds: [],
    courseIds: [],
    allowStudentUploads: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Élèves = users avec role Élève ou Élève adulte
        const studentsQuery = query(
          collection(db, 'users'),
          where('role', 'in', ['Élève', 'Élève adulte'])
        );
        const studentsSnapshot = await getDocs(studentsQuery);
        const studentsData = studentsSnapshot.docs.map(d => ({
          id: d.id,
          firstName: d.data().firstName || '',
          lastName: d.data().lastName || '',
          pseudo: d.data().pseudo || ''
        }));
        studentsData.sort((a, b) =>
          `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`)
        );
        setStudents(studentsData);

        // Cours (pour les dossiers de type "cours")
        const coursesSnapshot = await getDocs(collection(db, 'dance_courses'));
        const coursesData = coursesSnapshot.docs.map(d => ({
          id: d.id,
          title: d.data().title || 'Cours sans titre'
        }));
        coursesData.sort((a, b) => a.title.localeCompare(b.title));
        setCourses(coursesData);

        if (id) {
          const folderDoc = await getDoc(doc(db, 'resource_folders', id));
          if (folderDoc.exists()) {
            const data = folderDoc.data();
            setFormData({
              name: data.name || '',
              type: data.type || 'students',
              studentIds: data.studentIds || [],
              courseIds: data.courseIds || [],
              allowStudentUploads: !!data.allowStudentUploads
            });
          }
        }

        setLoading(false);
      } catch (err) {
        console.error('Erreur lors du chargement:', err);
        setError('Erreur lors du chargement des données');
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const toggleInList = (listName, value) => {
    setFormData(prev => {
      const list = prev[listName];
      return {
        ...prev,
        [listName]: list.includes(value)
          ? list.filter(v => v !== value)
          : [...list, value]
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.name.trim()) {
      setError('Veuillez saisir un nom pour le dossier.');
      return;
    }
    if (formData.type === 'students' && formData.studentIds.length === 0) {
      setError('Veuillez sélectionner au moins un élève, ou choisissez un autre type de dossier.');
      return;
    }
    if (formData.type === 'courses' && formData.courseIds.length === 0) {
      setError('Veuillez sélectionner au moins un cours.');
      return;
    }

    setSaving(true);

    try {
      const dataToSave = {
        name: formData.name.trim(),
        type: formData.type,
        studentIds: formData.type === 'students' ? formData.studentIds : [],
        courseIds: formData.type === 'courses' ? formData.courseIds : [],
        allowStudentUploads: formData.allowStudentUploads
      };

      if (id) {
        await updateDoc(doc(db, 'resource_folders', id), dataToSave);
      } else {
        await addDoc(collection(db, 'resource_folders'), {
          ...dataToSave,
          createdBy: auth.currentUser?.uid || null,
          createdAt: serverTimestamp()
        });
      }

      navigate('/resources');
    } catch (err) {
      console.error("Erreur lors de l'enregistrement:", err);
      setError(err.message || "Une erreur est survenue lors de l'enregistrement.");
      setSaving(false);
    }
  };

  const filteredStudents = students.filter(s => {
    const fullName = `${s.firstName} ${s.lastName} ${s.pseudo}`.toLowerCase();
    return fullName.includes(studentSearch.toLowerCase());
  });

  const checkboxListStyle = {
    maxHeight: 260,
    overflowY: 'auto',
    border: '1px solid #ddd',
    borderRadius: 8,
    padding: 10
  };

  const checkboxRowStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 4px',
    cursor: 'pointer'
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Chargement en cours...</p>
      </div>
    );
  }

  return (
    <div className="resource-form-container">
      <h1>{id ? 'Modifier' : 'Créer'} un dossier de ressources</h1>
      <p style={{ color: '#666', marginBottom: 20 }}>
        Un dossier vous permet de partager des ressources avec des élèves précis,
        avec tous les élèves d'un ou plusieurs cours, ou avec tout le monde.
      </p>

      {error && (
        <div className="error-message">
          <p>{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Nom du dossier *</label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Ex: Corrections perso Léa, Chorés Hip Hop Ados..."
            required
          />
        </div>

        <div className="form-group">
          <label>Type de dossier *</label>
          <select name="type" value={formData.type} onChange={handleChange} required>
            <option value="students">Élèves spécifiques</option>
            <option value="courses">Un ou plusieurs cours (élèves inscrits)</option>
            <option value="general">Général (visible par tous)</option>
          </select>
        </div>

        {formData.type === 'students' && (
          <div className="form-group">
            <label>Élèves concernés *</label>
            <input
              type="text"
              placeholder="Rechercher un élève..."
              value={studentSearch}
              onChange={(e) => setStudentSearch(e.target.value)}
              style={{ marginBottom: 10 }}
            />
            <div style={checkboxListStyle}>
              {filteredStudents.length === 0 ? (
                <p style={{ color: '#999', margin: 0 }}>Aucun élève trouvé</p>
              ) : (
                filteredStudents.map(student => (
                  <label key={student.id} style={checkboxRowStyle}>
                    <input
                      type="checkbox"
                      checked={formData.studentIds.includes(student.id)}
                      onChange={() => toggleInList('studentIds', student.id)}
                    />
                    {student.firstName} {student.lastName}
                    {student.pseudo ? ` (${student.pseudo})` : ''}
                  </label>
                ))
              )}
            </div>
            <small>{formData.studentIds.length} élève(s) sélectionné(s)</small>
          </div>
        )}

        {formData.type === 'courses' && (
          <div className="form-group">
            <label>Cours concernés *</label>
            <div style={checkboxListStyle}>
              {courses.length === 0 ? (
                <p style={{ color: '#999', margin: 0 }}>Aucun cours trouvé</p>
              ) : (
                courses.map(course => (
                  <label key={course.id} style={checkboxRowStyle}>
                    <input
                      type="checkbox"
                      checked={formData.courseIds.includes(course.id)}
                      onChange={() => toggleInList('courseIds', course.id)}
                    />
                    {course.title}
                  </label>
                ))
              )}
            </div>
            <small>
              {formData.courseIds.length} cours sélectionné(s) — le dossier est visible
              par tous les élèves inscrits à au moins un de ces cours.
            </small>
          </div>
        )}

        <div className="form-group">
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input
              type="checkbox"
              name="allowStudentUploads"
              checked={formData.allowStudentUploads}
              onChange={(e) =>
                setFormData(prev => ({ ...prev, allowStudentUploads: e.target.checked }))
              }
            />
            Autoriser les élèves de ce dossier à ajouter eux-mêmes des photos/vidéos
          </label>
          <small>
            Toute ressource ajoutée par un élève reste en attente de validation avant
            d'être visible par les autres.
          </small>
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="cancel-button"
            onClick={() => navigate('/resources')}
            disabled={saving}
          >
            Annuler
          </button>
          <button type="submit" className="submit-button" disabled={saving}>
            {saving ? 'Enregistrement...' : id ? 'Mettre à jour' : 'Créer le dossier'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default FolderForm;